/**
 * Google Analytics 4 Service
 *
 * Handles all analytics tracking for RewordGenie.
 * Includes page views, user events, and conversion tracking.
 */

import ReactGA from 'react-ga4';

// GA4 measurement ID. Configurable via env; falls back to the production
// property so analytics works in prod builds even without an .env entry.
// (GA measurement IDs are public — they appear in the page source.)
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-T8JHHGC40D';
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Initialize Google Analytics
 * Call this once when the app starts
 */
export const initGA = () => {
  if (MEASUREMENT_ID && !isDevelopment) {
    try {
      ReactGA.initialize(MEASUREMENT_ID, {
        gaOptions: {
          send_page_view: false, // We'll send page views manually
        },
      });
      console.log('[Analytics] Google Analytics initialized');
    } catch (error) {
      console.error('[Analytics] Failed to initialize GA:', error);
    }
  } else if (isDevelopment) {
    console.log('[Analytics] Skipping GA in development mode');
  } else {
    console.warn('[Analytics] GA_MEASUREMENT_ID not found in environment variables');
  }
};

/**
 * Track page view
 * Call this on route changes
 */
export const logPageView = (path, title) => {
  if (!MEASUREMENT_ID || isDevelopment) return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path || window.location.pathname,
      title: title || document.title,
    });
    console.log('[Analytics] Page view:', path || window.location.pathname);
  } catch (error) {
    console.error('[Analytics] Failed to log page view:', error);
  }
};

/**
 * Track custom event
 * @param {string} category - Event category (e.g., 'User', 'Content', 'Social')
 * @param {string} action - Event action (e.g., 'Sign Up', 'Generate', 'Publish')
 * @param {string} label - Event label (optional)
 * @param {number} value - Event value (optional)
 */
export const logEvent = (category, action, label, value) => {
  if (!MEASUREMENT_ID || isDevelopment) return;

  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
    console.log('[Analytics] Event:', category, action, label);
  } catch (error) {
    console.error('[Analytics] Failed to log event:', error);
  }
};

/**
 * Pre-defined events for common user actions
 */
export const trackEvents = {
  // ==================
  // User Events
  // ==================

  /**
   * User signs up for a new account
   */
  signup: (method = 'email') => {
    logEvent('User', 'Sign Up', method);
  },

  /**
   * User logs in
   */
  login: (method = 'email') => {
    logEvent('User', 'Login', method);
  },

  /**
   * User logs out
   */
  logout: () => {
    logEvent('User', 'Logout');
  },

  /**
   * User completes onboarding
   */
  onboardingComplete: () => {
    logEvent('User', 'Onboarding Complete');
  },

  // ==================
  // Content Generation
  // ==================

  /**
   * User generates AI content
   * @param {string} templateId - Template used for generation
   */
  generateContent: (templateId) => {
    logEvent('Content', 'Generate', templateId);
  },

  /**
   * User regenerates content
   */
  regenerateContent: () => {
    logEvent('Content', 'Regenerate');
  },

  /**
   * User copies generated content
   */
  copyContent: () => {
    logEvent('Content', 'Copy');
  },

  /**
   * User saves generated content
   */
  saveContent: () => {
    logEvent('Content', 'Save');
  },

  // ==================
  // Template Management
  // ==================

  /**
   * User creates a new template
   */
  createTemplate: () => {
    logEvent('Template', 'Create');
  },

  /**
   * User edits a template
   */
  editTemplate: (templateId) => {
    logEvent('Template', 'Edit', templateId);
  },

  /**
   * User duplicates a template
   */
  duplicateTemplate: (templateId) => {
    logEvent('Template', 'Duplicate', templateId);
  },

  /**
   * User deletes a template
   */
  deleteTemplate: (templateId) => {
    logEvent('Template', 'Delete', templateId);
  },

  /**
   * User uploads an image (logo/background)
   */
  uploadImage: (type) => {
    logEvent('Template', 'Upload Image', type); // 'logo' or 'background'
  },

  // ==================
  // Publishing
  // ==================

  /**
   * User publishes a post immediately
   * @param {string} platform - Social platform ('facebook', 'instagram', 'twitter')
   */
  publishPost: (platform) => {
    logEvent('Publish', 'Post Published', platform);
  },

  /**
   * User schedules a post for later
   * @param {string} platform - Social platform
   */
  schedulePost: (platform) => {
    logEvent('Publish', 'Post Scheduled', platform);
  },

  /**
   * User downloads post for manual publishing
   */
  downloadPost: () => {
    logEvent('Publish', 'Download');
  },

  // ==================
  // Social Connections
  // ==================

  /**
   * User connects a social media account
   * @param {string} platform - Platform connected
   */
  connectSocial: (platform) => {
    logEvent('Social', 'Connect', platform);
  },

  /**
   * User disconnects a social media account
   * @param {string} platform - Platform disconnected
   */
  disconnectSocial: (platform) => {
    logEvent('Social', 'Disconnect', platform);
  },

  /**
   * User selects a page/account to publish to
   * @param {string} platform - Platform
   */
  selectPage: (platform) => {
    logEvent('Social', 'Select Page', platform);
  },

  // ==================
  // Analytics & Insights
  // ==================

  /**
   * User views analytics dashboard
   */
  viewAnalytics: () => {
    logEvent('Analytics', 'View Dashboard');
  },

  /**
   * User views detailed post insights
   */
  viewPostInsights: (postId) => {
    logEvent('Analytics', 'View Post Insights', postId);
  },

  /**
   * User refreshes analytics data
   */
  refreshAnalytics: () => {
    logEvent('Analytics', 'Refresh Data');
  },

  // ==================
  // Team Management
  // ==================

  /**
   * User invites a team member
   */
  inviteTeamMember: () => {
    logEvent('Team', 'Invite Member');
  },

  /**
   * User accepts a team invite
   */
  acceptTeamInvite: () => {
    logEvent('Team', 'Accept Invite');
  },

  /**
   * User removes a team member
   */
  removeTeamMember: () => {
    logEvent('Team', 'Remove Member');
  },

  // ==================
  // Settings & Profile
  // ==================

  /**
   * User updates workspace settings
   */
  updateWorkspace: () => {
    logEvent('Settings', 'Update Workspace');
  },

  /**
   * User updates profile
   */
  updateProfile: () => {
    logEvent('Settings', 'Update Profile');
  },

  /**
   * User changes language
   * @param {string} language - New language code
   */
  changeLanguage: (language) => {
    logEvent('Settings', 'Change Language', language);
  },

  // ==================
  // Errors
  // ==================

  /**
   * Track errors for debugging
   * @param {string} errorType - Type of error
   * @param {string} errorMessage - Error message
   */
  trackError: (errorType, errorMessage) => {
    logEvent('Error', errorType, errorMessage);
  },
};

/**
 * Set user ID for tracking across sessions
 * Call this after user logs in
 */
export const setUserId = (userId) => {
  if (!MEASUREMENT_ID || isDevelopment) return;

  try {
    ReactGA.set({ userId });
    console.log('[Analytics] User ID set:', userId);
  } catch (error) {
    console.error('[Analytics] Failed to set user ID:', error);
  }
};

/**
 * Set user properties
 * Useful for segmentation and analysis
 */
export const setUserProperties = (properties) => {
  if (!MEASUREMENT_ID || isDevelopment) return;

  try {
    ReactGA.set(properties);
    console.log('[Analytics] User properties set:', properties);
  } catch (error) {
    console.error('[Analytics] Failed to set user properties:', error);
  }
};

export default {
  initGA,
  logPageView,
  logEvent,
  trackEvents,
  setUserId,
  setUserProperties,
};
